/**
 * Mapeo `EstadoExpediente → EtapaPanel` (ADR 016).
 *
 * Los 37 estados del state machine (ADR 004) se proyectan a 12 etapas
 * semánticas que el estudiante ve en su panel. Las etapas son
 * **monotónicas**: cada estado pertenece a una sola etapa, y los
 * sub-ciclos de observación (PROYECTO_OBSERVADO, BORRADOR_OBSERVADO, etc.)
 * se quedan en la misma etapa visual.
 *
 * Dos estados terminales especiales (`EXPEDIENTE_OBSERVADO`,
 * `TRAMITE_SUSPENDIDO`) NO mapean a ninguna etapa: el panel los detecta y
 * muestra un banner especial en lugar del stepper lineal.
 *
 * Patrón `Record<EstadoExpediente, EtapaPanel | null>` con typecheck
 * exhaustivo: agregar un nuevo `EstadoExpediente` al enum sin agregarlo
 * acá rompe la compilación (OCP forzado).
 */

import { EstadoExpediente, type TipoDocumento } from "./enums";

export type EtapaPanel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface EtapaPanelDefinicion {
  numero: EtapaPanel;
  titulo: string;
  descripcionCorta: string;
}

/** Las 12 etapas, en orden visual (1 = primera, 12 = última). */
export const ETAPAS_PANEL: readonly EtapaPanelDefinicion[] = [
  {
    numero: 1,
    titulo: "Solicitud de designación de asesor",
    descripcionCorta: "Propone un asesor a través del handshake.",
  },
  {
    numero: 2,
    titulo: "Aceptación del asesor",
    descripcionCorta: "El asesor revisa la propuesta y acepta o rechaza.",
  },
  {
    numero: 3,
    titulo: "Designación oficial por el Decanato",
    descripcionCorta: "El Decanato evalúa la solicitud formal.",
  },
  {
    numero: 4,
    titulo: "Resolución decanal de asesor",
    descripcionCorta: "Resolución firmada por el Decanato.",
  },
  {
    numero: 5,
    titulo: "Elaboración del proyecto",
    descripcionCorta: "Trabajas el proyecto con tu asesor.",
  },
  {
    numero: 6,
    titulo: "Conformidad del asesor",
    descripcionCorta: "El asesor emite conformidad sobre el proyecto.",
  },
  {
    numero: 7,
    titulo: "Revisión del jurado (proyecto)",
    descripcionCorta: "Tres titulares evalúan el proyecto.",
  },
  {
    numero: 8,
    titulo: "Ejecución del proyecto",
    descripcionCorta: "Hasta 18 meses para ejecutar la investigación.",
  },
  {
    numero: 9,
    titulo: "Presentación del borrador",
    descripcionCorta: "Presentas el borrador final al jurado.",
  },
  {
    numero: 10,
    titulo: "Revisión del jurado (borrador)",
    descripcionCorta: "Dictamen de suficiencia del borrador.",
  },
  {
    numero: 11,
    titulo: "Antiplagio y programación",
    descripcionCorta: "Control de similitud y fecha de sustentación.",
  },
  {
    numero: 12,
    titulo: "Sustentación y titulación",
    descripcionCorta: "Acto académico final y emisión del título.",
  },
] as const;

/**
 * Mapeo exhaustivo (typecheck): cada `EstadoExpediente` declara su etapa.
 * Los terminales fuera del flujo lineal devuelven `null` — el panel los
 * trata con un banner especial.
 */
export const ETAPA_POR_ESTADO: Record<EstadoExpediente, EtapaPanel | null> = {
  // Etapa 1 — Solicitud de designación de asesor.
  [EstadoExpediente.BORRADOR_SOLICITUD]: 1,
  [EstadoExpediente.ASESORIA_SOLICITADA]: 2, // ya creó la propuesta → etapa 2

  // Etapa 2 — Aceptación del asesor (la cubre `ASESORIA_SOLICITADA` arriba).

  // Etapa 3 — Designación oficial por el Decanato.
  [EstadoExpediente.ASESORIA_ACEPTADA]: 3,
  [EstadoExpediente.SOLICITUD_PRESENTADA]: 3,
  [EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO]: 3,
  [EstadoExpediente.SOLICITUD_OBSERVADA]: 3,
  [EstadoExpediente.SOLICITUD_RECHAZADA]: 3, // se queda visualmente acá; el banner se encarga

  // Etapa 4 — Resolución decanal de asesor (transitorio).
  [EstadoExpediente.ASESOR_DESIGNADO]: 4,

  // Etapa 5 — Elaboración del proyecto. Incluye el sub-estado
  // PROYECTO_EN_REVISION_ASESOR (rebote estudiante↔asesor, ADR 019):
  // visualmente sigue siendo "elaboración" en el stepper porque es parte
  // del mismo bloque; el panel lo diferencia con un componente inline.
  [EstadoExpediente.PROYECTO_EN_ELABORACION]: 5,
  [EstadoExpediente.PROYECTO_EN_REVISION_ASESOR]: 5,

  // Etapa 6 — Conformidad del asesor (transitorio antes de jurado).
  [EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR]: 6,

  // Etapa 7 — Revisión del jurado (proyecto).
  [EstadoExpediente.JURADO_DESIGNADO]: 7,
  [EstadoExpediente.PROYECTO_EN_REVISION_JURADO]: 7,
  [EstadoExpediente.PROYECTO_OBSERVADO]: 7,
  [EstadoExpediente.PROYECTO_RECHAZADO]: 7,
  [EstadoExpediente.PROYECTO_APROBADO]: 7,

  // Etapa 8 — Ejecución del proyecto.
  [EstadoExpediente.TESIS_EN_EJECUCION]: 8,
  [EstadoExpediente.PLAZO_EJECUCION_VENCIDO]: 8,

  // Etapa 9 — Presentación del borrador (transitorio).
  [EstadoExpediente.BORRADOR_TESIS_PRESENTADO]: 9,

  // Etapa 10 — Revisión del jurado (borrador).
  [EstadoExpediente.BORRADOR_EN_REVISION_JURADO]: 10,
  [EstadoExpediente.BORRADOR_OBSERVADO]: 10,
  [EstadoExpediente.BORRADOR_SUBSANADO]: 10,
  [EstadoExpediente.BORRADOR_INSUFICIENTE]: 10,
  [EstadoExpediente.BORRADOR_SUFICIENTE]: 10,

  // Etapa 11 — Antiplagio + programación sustentación.
  [EstadoExpediente.EN_CONTROL_ANTIPLAGIO]: 11,
  [EstadoExpediente.ANTIPLAGIO_OBSERVADO]: 11,
  [EstadoExpediente.ANTIPLAGIO_APROBADO]: 11,
  [EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA]: 11,
  [EstadoExpediente.SUSTENTACION_PROGRAMADA]: 11,
  [EstadoExpediente.SUSTENTACION_REPROGRAMADA]: 11,

  // Etapa 12 — Sustentación y titulación.
  [EstadoExpediente.SUSTENTACION_REALIZADA]: 12,
  [EstadoExpediente.SUSTENTACION_APROBADA]: 12,
  [EstadoExpediente.SUSTENTACION_DESAPROBADA]: 12,
  [EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO]: 12,
  [EstadoExpediente.TITULO_EN_TRAMITE]: 12,
  [EstadoExpediente.TITULO_OTORGADO]: 12,

  // Terminales especiales — banner, no stepper.
  [EstadoExpediente.EXPEDIENTE_OBSERVADO]: null,
  [EstadoExpediente.TRAMITE_SUSPENDIDO]: null,
};

/**
 * Devuelve la etapa actual del expediente, o `null` si está en un estado
 * terminal especial (el panel debe mostrar un banner).
 */
export function etapaDeEstado(estado: EstadoExpediente): EtapaPanel | null {
  return ETAPA_POR_ESTADO[estado];
}

/**
 * Devuelve la definición completa de una etapa (título + descripción).
 */
export function definicionEtapa(numero: EtapaPanel): EtapaPanelDefinicion {
  // `numero` es 1..12; el array es 0-indexed.
  const def = ETAPAS_PANEL[numero - 1];
  if (!def) {
    throw new Error(`Etapa ${numero} no existe en ETAPAS_PANEL`);
  }
  return def;
}

/**
 * Helper de progreso lineal: cuántos pasos están completados al estar en
 * una etapa dada. Convención: "completados" = pasos estrictamente
 * anteriores al actual. La etapa actual NO cuenta como completada hasta
 * que el expediente avance a la siguiente.
 *
 * Si el estado es terminal especial (`null`), devuelve 0 (sin progreso
 * lineal — banner se encarga).
 */
export function pasosCompletados(estado: EstadoExpediente): number {
  const etapa = etapaDeEstado(estado);
  if (etapa === null) return 0;
  return etapa - 1;
}

/**
 * Porcentaje de progreso 0..100 redondeado al entero.
 * `TITULO_OTORGADO` → 100 (caso especial: el último estado de la última
 * etapa cierra el viaje completo).
 */
export function porcentajeProgreso(estado: EstadoExpediente): number {
  if (estado === EstadoExpediente.TITULO_OTORGADO) return 100;
  const etapa = etapaDeEstado(estado);
  if (etapa === null) return 0;
  // (etapa - 1) / 12 * 100 — la etapa en curso no cuenta como completa.
  return Math.round(((etapa - 1) / 12) * 100);
}

// ============================================================================
// Wire del endpoint `GET /expedientes/:id/panel-estudiante` (ADR 016)
// ============================================================================

/** Urgencia del plazo, alineado a las categorías cromáticas del frontend. */
export type UrgenciaPlazo = "ok" | "proximo" | "critico" | "vencido";

/** Quién es el próximo actor que debe accionar en el flujo. */
export type ProximoActor = "ESTUDIANTE" | "ASESOR" | "JURADO" | "DECANATO" | "VRI" | "REPOSITORIO";

export interface PanelEstudianteWire {
  expediente: {
    id: string;
    codigoExpediente: string;
    tituloPropuesto: string;
    estadoActual: EstadoExpediente;
    etapaActual: EtapaPanel | null; // null si está en terminal especial
    iniciadoEn: string; // ISO
    fechaUltimaActualizacion: string; // ISO
  };
  progreso: {
    pasosCompletados: number; // 0..12
    totalPasos: 12;
    porcentaje: number; // 0..100
  };
  etapa: {
    numero: EtapaPanel;
    titulo: string;
    descripcion: string;
    siguiente: { numero: EtapaPanel; titulo: string } | null;
  } | null; // null si terminal especial
  asesor: {
    id: string;
    nombres: string;
    apellidos: string;
    correo: string;
    activo: boolean;
  } | null;
  plazo: {
    fechaLimite: string | null; // ISO
    diasRestantes: number | null;
    urgencia: UrgenciaPlazo | null;
  };
  observacionesPendientes: {
    fuente: "ASESOR" | "JURADO" | "VRI" | null;
    cantidad: number;
    items: Array<{
      autor: string; // "Dr. Juan Pérez (Asesor)"
      cuando: string; // ISO
      texto: string;
    }>;
  };
  ultimaVersionDocPrincipal: {
    id: string;
    tipoDocumento: TipoDocumento;
    nombreArchivo: string;
    version: number;
    fechaSubida: string; // ISO
    tamanoBytes: number;
  } | null;
  proximaAccion: {
    quien: ProximoActor;
    titulo: string;
    cta?: { label: string; href?: string; accionModal?: string };
  };
  /**
   * Cuando `expediente.estadoActual` es terminal especial
   * (`EXPEDIENTE_OBSERVADO`, `TRAMITE_SUSPENDIDO`), el panel debe renderizar
   * un banner en vez del flujo lineal. Aquí va el motivo si está disponible.
   */
  bannerTerminal: { motivo: string } | null;
  /**
   * `true` si el estudiante puede iniciar un nuevo expediente desde aquí
   * (ADR 017 — unicidad del expediente activo). Lo deriva el backend a
   * partir de la clase del estado actual:
   *  - `null` (sin expediente): true.
   *  - `CERRADO_REGULAR`: true (puede empezar otro).
   *  - `VIVO` o `CERRADO_BLOQUEANTE`: false (tiene un trámite vivo o anómalo).
   *
   * El frontend usa este flag para mostrar/ocultar el CTA "Nueva
   * solicitud". `NuevaSolicitudPage` lo revalida al cargar (defense in
   * depth); el backend también lo verifica en `ExpedientesService.crear`.
   */
  puedeCrearNuevo: boolean;
}
